"use client";

import * as React from "react";
import { cn } from "@/lib/cn";
import { ink } from "@/lib/ink";

// ЗАЧЕМ. Плашки статусов рисовались руками в полусотне файлов: где-то заливка
// 12 %, где-то 16 %, где-то палитра Tailwind вместо переменных темы, размеры
// от text-[11px] до text-[11px]. Один компонент — одна форма во всём портале.
//
// Цвет задаётся ЛИБО тоном (`tone`, из переменных темы), ЛИБО произвольным
// значением (`color`) — так работают статусы, у которых цвет лежит в карте
// вида STATUS_COLOR[status].

export type BadgeTone = "neutral" | "primary" | "success" | "warning" | "danger" | "info";
export type BadgeVariant = "soft" | "outline" | "solid";
export type BadgeSize = "xs" | "sm" | "md";

const TONE_COLOR: Record<BadgeTone, string> = {
  neutral: "var(--textMuted)",
  primary: "var(--primary)",
  success: "var(--success)",
  warning: "var(--warning)",
  danger: "var(--danger)",
  info: "var(--info, var(--primary))",
};

const SIZE: Record<BadgeSize, string> = {
  // Кегль у xs и sm ОДИН — 11px, единственная мелкая ступень портала; xs от sm
  // отличается только отступами (плашка уже, а не буквы мельче).
  xs: "px-1.5 py-0.5 text-[11px] gap-1",
  sm: "px-2 py-0.5 text-[11px] gap-1",
  // Размеры в пикселях, не в rem-классах: rem в портале = 12px, из-за чего
  // text-[11px] (0.75rem) даёт 9px — меньше, чем sm. См. дизайн-систему.
  md: "px-2.5 py-1 text-[12px] gap-1.5",
};

const DOT: Record<BadgeSize, string> = { xs: "size-1", sm: "size-1.5", md: "size-1.5" };

// Единые пропорции заливки и рамки — раньше у каждого места были свои.
const FILL = 14;
const BORDER = 34;

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  /** Тон из палитры темы. Игнорируется, если задан `color`. */
  tone?: BadgeTone;
  /** Произвольный цвет — для статусов, чей цвет приходит из карты или с бэкенда. */
  color?: string;
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** Точка слева — для статусов, где важен «огонёк», а не иконка. */
  dot?: boolean;
  /** Иконка слева от текста. */
  icon?: React.ReactNode;
  /**
   * Слот после текста — вне обрезки, поэтому годится для абсолютно
   * спозиционированных вещей: например невидимого триггера выбора статуса,
   * который лежит поверх всей плашки (не забудьте `className="relative"`).
   */
  after?: React.ReactNode;
}

export function Badge({
  tone = "neutral",
  color,
  variant = "soft",
  size = "sm",
  dot,
  icon,
  after,
  className,
  children,
  style,
  ...rest
}: BadgeProps) {
  const c = color ?? TONE_COLOR[tone];
  const solid = variant === "solid";

  return (
    <span
      className={cn(
        // Радиус — ИЗ ТЕМЫ, а не капсула. Пилюля была уместна при общем радиусе
        // 18, но портал перешёл на угловатые формы, и круглые чипы в столбце
        // прямоугольных строк выбиваются (Максим, 26.08.2026).
        // ⚠️ Точка-маркер внутри чипа (DOT ниже) остаётся круглой: это точка, а
        // не плашка, и прямоугольной она быть не может по смыслу.
        "inline-flex max-w-full items-center rounded-[var(--radius-md)] font-semibold leading-none whitespace-nowrap",
        SIZE[size],
        className,
      )}
      style={{
        // Текст — «чернилами» (см. lib/ink.ts): заливка 14 % слишком бледная,
        // чтобы держать на себе исходный сигнальный цвет. Заливка и рамка
        // остаются прежними — меняется только читаемость надписи.
        color: solid ? "#fff" : ink(c),
        background: solid ? c : `color-mix(in srgb, ${c} ${FILL}%, transparent)`,
        border:
          variant === "outline"
            ? `1px solid color-mix(in srgb, ${c} ${BORDER}%, transparent)`
            : undefined,
        ...style,
      }}
      {...rest}
    >
      {dot && (
        <span
          className={cn("shrink-0 rounded-full", DOT[size])}
          style={{ background: solid ? "#fff" : c }}
        />
      )}
      {icon && <span className="inline-flex shrink-0 items-center">{icon}</span>}
      {/* Простой текст обрезаем многоточием. Составное содержимое (число +
          подпись, вложенные спаны) отдаём как есть — иначе зазор между его
          частями съедается: они перестают быть детьми flex-контейнера. */}
      {typeof children === "string" || typeof children === "number" ? (
        <span className="min-w-0 truncate">{children}</span>
      ) : (
        children
      )}
      {after}
    </span>
  );
}
